import { StoreError } from '@/types/storeError.interface';
import {
  IApplication,
  IndexedSignedApplications,
  SignedApplicationFormPdf,
} from '@/typings';
import {
  createStoreSlice as createSlice,
  type PayloadAction,
} from '@/store/core';
import { RootState } from '../store';

interface ReviewAndSubmitState {
  loading: boolean;
  error: StoreError | null;
  signedApplications: IndexedSignedApplications;
  /** True only once a signed-PDF fetch has actually rejected; distinct from `error`, which review/submit also set. */
  signedApplicationFetchFailed: boolean;
}

const initialState: ReviewAndSubmitState = {
  loading: false,
  error: null,
  signedApplications: {},
  signedApplicationFetchFailed: false,
};

export const reviewAndSubmitSlice = createSlice({
  name: 'reviewAndSubmit',
  initialState,
  reducers: {
    reviewAndSubmitLoading: state => {
      state.loading = true;
      state.signedApplicationFetchFailed = false;
    },
    fetchSignedApplicationPdf: (
      state,
      action: PayloadAction<SignedApplicationFormPdf>,
    ) => {
      state.loading = false;
      state.error = null;
      state.signedApplicationFetchFailed = false;
      state.signedApplications[action.payload.applicationId] = action.payload;
    },
    initSignedApplicationPdfViewedPages: (
      state,
      action: PayloadAction<{
        applicationId: IApplication['id'];
        viewedPages: boolean[];
      }>,
    ) => {
      state.signedApplications[action.payload.applicationId] = {
        ...state.signedApplications[action.payload.applicationId],
        viewedPages: action.payload.viewedPages,
      };
    },
    markAsViewedSignedApplicationPdfViewedPages: (
      state,
      action: PayloadAction<{
        applicationId: IApplication['id'];
        pageNumber: number;
      }>,
    ) => {
      const newViewedPages = [
        ...state.signedApplications[action.payload.applicationId].viewedPages,
      ];
      newViewedPages[action.payload.pageNumber - 1] = true;
      state.signedApplications[action.payload.applicationId] = {
        ...state.signedApplications[action.payload.applicationId],
        viewedPages: newViewedPages,
      };
    },
    updateSignedApplicationPdfIsDownloaded: (
      state,
      action: PayloadAction<IApplication['id']>,
    ) => {
      state.signedApplications[action.payload] = {
        ...state.signedApplications[action.payload],
        isDownloaded: true,
      };
    },
    setReviewAndSubmitError: (state, action: PayloadAction<StoreError>) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Dedicated to the signed-PDF fetch, unlike `setReviewAndSubmitError`
    // (also used by the review/submit thunks): the page needs to know
    // specifically that fetching the document failed, not that some
    // unrelated request on this slice did.
    setSignedApplicationFetchFailed: (
      state,
      action: PayloadAction<StoreError>,
    ) => {
      state.loading = false;
      state.error = action.payload;
      state.signedApplicationFetchFailed = true;
    },
  },
});

export const {
  reviewAndSubmitLoading,
  fetchSignedApplicationPdf,
  initSignedApplicationPdfViewedPages,
  markAsViewedSignedApplicationPdfViewedPages,
  updateSignedApplicationPdfIsDownloaded,
  setReviewAndSubmitError,
  setSignedApplicationFetchFailed,
} = reviewAndSubmitSlice.actions;

export const selectReviewAndSubmit = (state: RootState) =>
  state.reviewAndSubmit;
